import React, { useState, Fragment, useEffect } from 'react';
import { createContract, addMassive, bulkCreate } from 'api/actions/contracts';
import { PickType, Success, SuccessMultiple } from './Steps';
import { useCancellablePromises } from 'utils';
import Header from './Header';
import { fetcher } from 'api/client';
import { uploadFileV2 } from 'api/actions/file';
import { useContractListState, useContractList } from 'context/contracts';
import Loader from './Loader';
import { useQueryClient, useMutation } from 'react-query';
import types from './Constants/types';
import clsx from 'clsx';

import TemplateStep from './Steps/TemplateStep';
import BlankStep from './Steps/BlankStep';
import QuestionnaireStep from './Steps/QuestionnaireStep';
import SourceStep from './Steps/SourceStep';
import MultipleStep from './Steps/MultipleStep';
import OldMultipleStep from './Steps/OldMultipleStep';
import MassiveStep from './Steps/MassiveStep';
import { useHistory, useParams } from 'react-router-dom';
import { StepContainer } from './Steps/StepContainer';

const CreateDocumentPanel = ({ isOpen, handleClose, referenceId = null, options = {},  }) => {
    const history = useHistory();
    const { childOf, recordId } = useParams();
    const queryClient = useQueryClient();
    const [documentType, setDocumentType] = useState(options.initialDocumentType || null);
    const [isLoading, setIsLoading] = useState(false);
    const [createdDocument, setCreatedDocument] = useState(null);
    const [createdDocumentErrors, setCreatedDocumentErrors] = useState([]);
    const { query: refetch } = useContractListState();
    const [isReady, setIsReady] = useState(!!options.initialDocumentType);
    const api = useCancellablePromises();
    // if (!isOpen) return null;
    const { mutate: bulkCreateeMutate, isLoading: isBulkCreating } = useMutation((payload) => fetcher(bulkCreate(payload)));

    useEffect(() => {
        setDocumentType(options.initialDocumentType)
        setIsReady(!!options.initialDocumentType)
    }, [options.initialDocumentType])

    const handleSelected = (val) => setDocumentType(val === documentType ? null : val);

    const closePanel = () => {
        reset();
        handleClose();
    };

    const reset = () => {
        setIsLoading(false);
        setIsReady(false);
        setDocumentType(null);
        setCreatedDocument(null);
    };

    const handleComplete = async (data, reference) => {
        const isFromLocal = documentType === 'source';

        const source = isFromLocal ? 'upload' : documentType;

        const usesTemplate = ['template', 'questionnaire', 'pagare', 'application'].includes(documentType);

        const shouldReference = referenceId || (reference && reference.join && reference.document);

        const fileInfo = {};

        try {
            setIsLoading(true);

            if (documentType === 'massive') {
                const { successful = [], errors = [] } = await fetcher(addMassive(data));

                setCreatedDocument(successful);

                if (errors.length) setCreatedDocumentErrors(errors);
            } else if (documentType !== 'multiple') {
                if (isFromLocal) {
                    const result = await fetcher(uploadFileV2(data.file));
                    fileInfo.result = result;
                    fileInfo.type = data.file?.name?.split('.').pop().toLowerCase().trim();
                }
                const doc = {
                    childOf: childOf ? childOf : undefined,
                    record: recordId ? recordId : undefined,
                    name: data.name,
                    editorType: 'basic',
                    saveAction: 'save',
                    prefilled: true,
                    source: source,
                    validationRequired: false,
                    participants: [],
                    tags: [],
                    ...(['application', 'questionnaire'].includes(documentType) && {
                        questions: data.questions,
                    }),
                    ...(usesTemplate && {
                        sourceTemplate: data.template.value,
                        sourceTemplateName: data.template.label,
                        participants: data.template.template.participants,
                        signatureType: data.template.template.signatureType,
                        workflow: data.template.template.workflow?._id ? data.template.template.workflow._id : undefined,
                        elements: data.template.template.elements || [],
                        type: data.template.template.type,
                        locked: data.template.template.locked,
                    }),
                    ...(isFromLocal && {
                        originalType: fileInfo.type,
                        originalFileType: fileInfo.type,
                        importedPdf: fileInfo.type.includes('pdf') ? fileInfo.result.url : undefined,
                        wordonline: ['docx', 'doc'].includes(fileInfo.type) ? fileInfo.result : undefined,
                        fileName: data.file.fileName,
                        metadata: data.file.uploadResult?.metadataId,
                    }),
                    ...(documentType === 'application' && {
                        isApplication: true,
                    }),
                    ...(shouldReference && {
                        setreference: referenceId || reference.document,
                        copyPartInfo: reference.participants,
                        copyMilestones: reference.copyMilestones,
                        isReference: referenceId || reference.document,
                    }),
                };
                let document = await fetcher(createContract(doc));
                setCreatedDocument(document);
            } else if (documentType === 'multiple') {
                const contracts = data.files.map(f => {
                    const fileType = f.file?.name?.split('.').pop().toLowerCase().trim();

                    return ({
                    name: f.file?.name,
                    editorType: 'basic',
                    saveAction: 'save',
                    prefilled: true,
                    source: source,
                    validationRequired: false,
                    validated: false,
                    participants: [],
                    tags: [],
                    originalType: fileType,
                    status: ['draft', 'send'].includes(data.uploadStatus) ? '' : data.uploadStatus,
                    importedPdf: fileType.includes('pdf') ? f.url : undefined,
                    wordonline: ['docx', 'doc'].includes(fileType) ? f.uploadResult : undefined,
                    fileName: f.file?.name,
                    uploadMethod: 'multiple',
                    // type: file.contractType,
                    ...(shouldReference && {
                        setreference: referenceId || reference.document,
                        copyPartInfo: reference.participants,
                        isReference: referenceId || reference.document,
                    }),
                })
            })
                bulkCreateeMutate({ contracts, layout: data.layout, action: data.uploadStatus, skipLayoutConfiguration: data.skipLayoutConfiguration }, {
                    onSuccess: (data) => {
                        setCreatedDocument(data.successful);
                        setCreatedDocumentErrors(data.errors)
                        queryClient.invalidateQueries('fetch-contract-list')
                    }
                })                

            } else {
                const createdFiles = [];

                for (const file of data.files) {
                    const uploadResult = await fetcher(uploadFileV2(file));

                    const fileType = file.name?.split('.').pop().toLowerCase().trim();

                    const data = await fetcher(
                        createContract({
                            name: file.contractName,
                            editorType: 'basic',
                            saveAction: 'save',
                            prefilled: true,
                            source: source,
                            validationRequired: false,
                            validated: false,
                            participants: [],
                            tags: [],
                            originalType: fileType,
                            status: file.contractStatus === 'EMPTY' ? '' : file.contractStatus,
                            importedPdf: fileType.includes('pdf') ? uploadResult.url : undefined,
                            wordonline: ['docx', 'doc'].includes(fileType) ? uploadResult : undefined,
                            fileName: file.name,
                            uploadMethod: 'multiple',
                            type: file.contractType,
                            ...(shouldReference && {
                                setreference: referenceId || reference.document,
                                copyPartInfo: reference.participants,
                                isReference: referenceId || reference.document,
                            }),
                        })
                    );

                    createdFiles.push(data);
                }

                setCreatedDocument(createdFiles);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
            refetch();
            queryClient.invalidateQueries('minidashboards-fetch');
        }
    };
    const openDocument = () => {
        api.clearPendingPromises();
        history.push(`/contracts/editor/${createdDocument._id}`);
    };
    const documentTypeSelector = () => {
        // Si hay documento es que ya fue procesado y se puede seguir al slide de success
        if (createdDocument) {
            const source = types.find((d) => d.type === documentType).label;
            return Array.isArray(createdDocument) ? (
                <SuccessMultiple contracts={createdDocument} errors={createdDocumentErrors} source={source} handleBack={reset} />
            ) : (
                <Success contract={createdDocument} source={source} handleBack={reset} openDocument={openDocument} />
            );
        }

        return {
            template: <TemplateStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} options={options} />,
            blank: <BlankStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} />,
            questionnaire: <QuestionnaireStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} />,
            application: <QuestionnaireStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} />,
            source: <SourceStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} />,
            multiple: <MultipleStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} />,
            massive: <MassiveStep handleBack={reset} onComplete={handleComplete} showReference={false} />,
            pagare: <TemplateStep handleBack={reset} onComplete={handleComplete} showReference={!referenceId} isPagare={true} options={options} />,
        }[documentType];
    };

    const handleSetIsReady = () => {
        setIsReady(true)
    }

    return (
        <div
            className="bg-white shadow position-fixed transition-all"
            style={{
                top: 0,
                height: '100%',
                right: isOpen ? 0 : '-550px',
                width: '550px',
                zIndex: 2,
            }}
        >
            <Header handleClose={closePanel} referenceId={referenceId} />
            <StepContainer
                className="h-100"
                form={
                    <div className="p-4 h-100">
                        {isLoading || isBulkCreating ? (
                            <Loader />
                        ) : isReady ? (
                            documentTypeSelector()
                        ) : (
                            <PickType
                                documentType={documentType}
                                handleSelected={handleSelected}
                                goToNext={handleSetIsReady}
                            />
                        )}
                    </div>
                }
            />
        </div>
    );
};

export default CreateDocumentPanel;
